import { keyName, parseKey } from '../../music/key';
import { formatRoman } from '../../music/roman';
import { chordFamily } from '../../data/observatory';
import { CHORD_WORDS } from '../copy';
import type { Depth } from '../hooks';
import { TerrainNode, wellRadius } from '../model';

interface Props {
  keyToken: string;
  nodes: TerrainNode[];
  current?: string | null;
  trail?: string[];
  depth: Depth;
  onPick?: (symbol: string) => void;
  size?: number;
  caption?: string;
}

const RINGS: Array<{ r: number; word: string }> = [
  { r: wellRadius(0.1), word: 'resting' },
  { r: wellRadius(0.4), word: 'leaning' },
  { r: wellRadius(0.7), word: 'pulling' },
  { r: wellRadius(1), word: 'urgent' },
];
const LABEL_ANGLE = (140 * Math.PI) / 180;

/**
 * The gravity well of one key, seen from above. Distance from the centre is how
 * hard a chord pulls; the angle follows the circle of fifths (dominant side to the
 * right, subdominant side to the left). Home is the centre.
 */
export function Well({ keyToken, nodes, current, trail = [], depth, onPick, size = 400, caption }: Props) {
  const S = size * 0.4;
  const c = size / 2;
  const px = (n: { x: number; y: number }) => ({ x: c + n.x * S, y: c + n.y * S });
  const byId = new Map(nodes.map((n) => [n.symbol, n]));
  const trailNodes = trail.map((s) => byId.get(s)).filter(Boolean) as TerrainNode[];
  const key = parseKey(keyToken);
  const centerWord = depth === 0 ? '' : depth === 1 ? 'home' : keyName(key);
  return (
    <svg viewBox={`0 0 ${size} ${size}`} role="img" aria-label={caption ?? `Gravity well of ${keyName(key)}`}>
      <defs>
        <radialGradient id="well-grad">
          <stop offset="0" className="st-rest" stopOpacity={0.22} />
          <stop offset="0.6" className="st-rest" stopOpacity={0.04} />
          <stop offset="1" className="st-tension" stopOpacity={0.08} />
        </radialGradient>
      </defs>
      <circle cx={c} cy={c} r={S * 1.04} fill="url(#well-grad)" />
      {RINGS.map((ring) => (
        <g key={ring.r}>
          <circle cx={c} cy={c} r={ring.r * S} fill="none" className="s-grid" strokeDasharray={ring.r === 1 ? undefined : '2 5'} />
          {depth >= 1 && (
            <text x={c + Math.cos(LABEL_ANGLE) * ring.r * S} y={c + Math.sin(LABEL_ANGLE) * ring.r * S - 3} textAnchor="middle" className="t-small" style={{ fontSize: 10 }}>
              {ring.word}
            </text>
          )}
        </g>
      ))}
      {depth >= 2 && (
        <g className="t-small" style={{ fontSize: 10.5 }}>
          <text x={c + S * 0.8} y={c - S * 1.02} textAnchor="middle">dominant side</text>
          <text x={c - S * 0.8} y={c - S * 1.02} textAnchor="middle">subdominant side</text>
        </g>
      )}
      <g key={keyToken} className="well-body">
        {trailNodes.length > 1 &&
          trailNodes.slice(1).map((n, i) => {
            const a = px(trailNodes[i]);
            const b = px(n);
            const opacity = (i + 1) / trailNodes.length;
            return <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} className="s-tension" strokeWidth={2} strokeOpacity={opacity * 0.8} strokeLinecap="round" />;
          })}
        {nodes.map((n) => {
          const p = px(n);
          const on = n.symbol === current;
          const r = on ? 19 : 15;
          const label = depth >= 2 ? formatRoman(n.symbol, true) : '';
          return (
            <g
              key={n.symbol}
              className="node"
              data-on={on}
              transform={`translate(${p.x},${p.y})`}
              onClick={onPick ? () => onPick(n.symbol) : undefined}
              role={onPick ? 'button' : undefined}
              tabIndex={onPick ? 0 : undefined}
              aria-label={onPick ? `Play ${formatRoman(n.symbol)}` : undefined}
              onKeyDown={
                onPick
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onPick(n.symbol);
                      }
                    }
                  : undefined
              }
              style={onPick ? undefined : { cursor: 'default' }}
            >
              {/* Hover names the chord at the level of the theory setting: a plain word, or its numeral. */}
              <title>{depth >= 2 ? formatRoman(n.symbol) : (CHORD_WORDS[chordFamily(n.symbol)] ?? 'a chord of this key')}</title>
              {on && <circle r={r + 9} className="f-tension" opacity={0.16} />}
              <circle r={r} className="dot" />
              {label && (
                <text textAnchor="middle" dy="0.35em" style={{ fontSize: label.length > 5 ? 10.5 : 13 }}>
                  {label}
                </text>
              )}
              {depth === 1 && on && CHORD_WORDS[chordFamily(n.symbol)] && (
                <g className="node-word" transform={`translate(0, ${r + 16})`}>
                  <rect x={-(CHORD_WORDS[chordFamily(n.symbol)].length * 3.4 + 8)} y={-10} width={CHORD_WORDS[chordFamily(n.symbol)].length * 6.8 + 16} height={20} rx={10} />
                  <text textAnchor="middle" dy="0.35em" style={{ fontSize: 12 }}>
                    {CHORD_WORDS[chordFamily(n.symbol)]}
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </g>
      {centerWord && (
        <text x={c} y={size - 10} textAnchor="middle" className="t-italic">
          {centerWord === 'home' ? 'the centre is home' : `centre: ${centerWord}`}
        </text>
      )}
    </svg>
  );
}
